import { persistStateTypes as PERSIST_STATE_TYPES } from 'a/reducers/persistStateReducer';
import { getTimestamp, getCurrentTimeInMs } from 'root/lib/DatetimeManager';
import {
  ACTIONS,
  PLACEMENT_TOOL,
  TRO_BENCHMARK,
  SCREENS,
  INTERSTITIAL_IDS,
  ASSESSMENT_PATHS,
  PLACEMENT_TOOL_ES,
} from '../../data/constants';
import GameIds from '../../../hub/data/GameIds';
import measureIds from '../../data/measureIds';
import * as utility from '../../lib/utilityFunctions';
import { PRODUCT_LECTURA } from '../../../../lib/constants';

export const initialState = {
  currentScreen: SCREENS.LOADING,
  currentInterstitial: '',
  currentMeasure: '',
  roundSessionIds: {
    rwrId: null,
    mtgIdStory1: null,
    mtgIdStory2: null,
    benchmarkId: null,
  },
  measuresCompleted: [],
  measureInProgress: {},
  measuresRemaining: [],
  didLoadSavedData: false,
  troData: {},
  isValidTroResult: true,
  benchmarkEOY: null, // APT or ARBA without placement/replacement - values will be APT, TRO-APT, or null
  hasStartedBenchmarkEOY: false, // not posting placement scores - check if student has started benchmark
  hasCompletedRWR: false, // for Benchmark EOY - check if student has finished RWR portion
  isBenchmarkModeOn: false, //  after error with TRO - teacher reset student to take TRO only
  isTroRequestResolved: false, // used with TRO data GET request - nec for loading screen/sending ipt_scores post request
  isTroRequestFailure: false, // TRO Results API failed - need to bypass redirect to TRO site
  results: [],
  metrics: {},
  // if we load directly into outro, it means we already sent results,
  // don't send again in interstitialContainer
  hasLoadedIntoOutro: false,
  teacherResetInfo: {
    BOY: {
      hasTroError: false, // student encountered any error during TRO that redirected back to AR
      resetCount: 0, // count of teacher reset clicks
      teacherReset: false, // teacher reset TRO in TD due to invalidation
    },
    MOY: {
      hasTroError: false,
      resetCount: 0,
      teacherReset: false,
    },
    EOY: {
      hasTroError: false,
      resetCount: 0,
      teacherReset: false,
    },
  },
  shouldDisplayAmpGlobalNav: false,
};

const getUpdatedMetricsData = (state) => {
  const { measureInProgress: { id, curriculum } } = state;
  let { metrics } = state;
  if (!id || !curriculum) {
    return metrics;
  }

  const currentMeasureKey = `${id}_${curriculum}`;
  const measureStartTimeKey = `${currentMeasureKey}_StartTime`;
  const measureEndTimeKey = `${currentMeasureKey}_EndTime`;
  const measureEndTime = getCurrentTimeInMs();
  // Take current timestamp in milliseconds and subtract start time to get time spent in measure
  const timeSpentInMeasure = measureEndTime - (Date.parse(metrics[measureStartTimeKey]) || 0);
  // Add time spent in most recent measure and add it to total time spent in assessment
  const timeSpentInIPT = (metrics.timeSpentInIPT ?? 0) + timeSpentInMeasure;

  metrics = {
    ...metrics,
    [measureEndTimeKey]: getTimestamp(measureEndTime),
    [`timeSpentIn${currentMeasureKey}`]: timeSpentInMeasure,
    timeSpentInIPT,
  };

  return metrics;
};

export default (state = initialState, { gameId, type, payload, stateData, product }) => {
  if (
    ![PLACEMENT_TOOL, TRO_BENCHMARK, PLACEMENT_TOOL_ES].includes(gameId)
    && type !== PERSIST_STATE_TYPES.REQUESTRESPONSE_GAMESTATE_ALL
  ) {
    return state;
  }

  switch (type) {
    case ACTIONS.SET_MEASURES_FOR_GRADE: {
      const { placements } = payload;
      const measures = [...payload.measures];
      const { metrics } = state;
      return {
        ...state,
        measuresRemaining: measures.map(measure => ({ ...measure })),
        currentMeasure: measures.shift(),
        placements,
        metrics: {
          ...metrics,
          LocalIPTStartTime: getTimestamp(),
        },
      };
    }
    case PERSIST_STATE_TYPES.REQUESTRESPONSE_GAMESTATE_ALL:
    case PERSIST_STATE_TYPES.REQUESTRESPONSE_GAMESTATE: {
      const isLecturaProduct = product === PRODUCT_LECTURA || gameId === PLACEMENT_TOOL_ES;
      const gameStateBucket = (isLecturaProduct) ? PLACEMENT_TOOL_ES : PLACEMENT_TOOL;
      let { GameData: { gameDetails: { localIPTEndReadingTime } = {} } = {} } = stateData[GameIds.METAGAME] ?? {};
      let { GameData: { gameDetails: { localIPTEndLecturaTime } = {} } = {} } = stateData[GameIds.METAGAME] ?? {};
      const { GameData: placementToolSavedData } = stateData[gameStateBucket] ?? {};
      const { GameData: troBenchmarkSavedData } = stateData[TRO_BENCHMARK] ?? {};

      localIPTEndLecturaTime = state.metrics.localIPTEndLecturaTime
        ? state.metrics.localIPTEndLecturaTime
        : localIPTEndLecturaTime;

      localIPTEndReadingTime = state.metrics.localIPTEndReadingTime
        ? state.metrics.localIPTEndReadingTime
        : localIPTEndReadingTime;

      const { isBenchmarkModeOn, isValidTroResult, troData } = state;
      if (placementToolSavedData || troBenchmarkSavedData) {
        try {
          let currentInterstitial = INTERSTITIAL_IDS.WELCOME_BACK;
          let hasLoadedIntoOutro = false;
          if (placementToolSavedData) {
            utility.validatePlacementToolData(placementToolSavedData);
            if (
              placementToolSavedData.measuresRemaining.length === 0
              && Object.keys(placementToolSavedData.measureInProgress).length === 0
            ) {
              hasLoadedIntoOutro = true;
              currentInterstitial = INTERSTITIAL_IDS.OUTRO;
            } else if (placementToolSavedData.measuresCompleted.length && !isValidTroResult && !isBenchmarkModeOn) {
              currentInterstitial = INTERSTITIAL_IDS.INVALID_TRO_RESULT;
            }
          }

          let { benchmarkEOY, hasStartedBenchmarkEOY, hasCompletedRWR, roundSessionIds, teacherResetInfo } = state;
          if (troBenchmarkSavedData) {
            benchmarkEOY = troBenchmarkSavedData.benchmarkEOY ?? null;
            if (benchmarkEOY) {
              currentInterstitial = INTERSTITIAL_IDS.INTRO;

              if (troBenchmarkSavedData.hasStartedBenchmarkEOY) {
                roundSessionIds = {
                  ...roundSessionIds,
                  benchmarkId: troBenchmarkSavedData.benchmarkId,
                  rwrId: troBenchmarkSavedData.rwrId,
                };
                hasStartedBenchmarkEOY = true;
                hasCompletedRWR = !!troBenchmarkSavedData.hasCompletedRWR;
                currentInterstitial = INTERSTITIAL_IDS.WELCOME_BACK;
              }

              if (
                (benchmarkEOY === ASSESSMENT_PATHS.APT && hasCompletedRWR)
                || (benchmarkEOY === ASSESSMENT_PATHS.ARBA && troData.is_complete)
              ) {
                hasLoadedIntoOutro = true;
                currentInterstitial = INTERSTITIAL_IDS.OUTRO;
              }

              if (!isValidTroResult) {
                currentInterstitial = INTERSTITIAL_IDS.INVALID_TRO_RESULT;
              }
            }

            teacherResetInfo = mergeTeacherResetInfo(teacherResetInfo, troBenchmarkSavedData);
          }

          const savedPlacementToolData = mergeData(state, placementToolSavedData);
          const roundSessionIdsValue = troBenchmarkSavedData?.hasStartedBenchmarkEOY
            ? roundSessionIds
            : savedPlacementToolData.roundSessionIds;

          return {
            ...savedPlacementToolData,
            didLoadSavedData: true,
            currentScreen: SCREENS.INTERSTITIAL,
            currentInterstitial,
            hasLoadedIntoOutro,
            benchmarkEOY,
            hasStartedBenchmarkEOY,
            hasCompletedRWR,
            roundSessionIds: roundSessionIdsValue,
            teacherResetInfo,
            metrics: {
              ...state.metrics,
              localIPTEndReadingTime,
              localIPTEndLecturaTime,
            },
          };
        } catch (e) {
          console.error(
            'Placement tool: There was a problem with the incoming saved data from server.\n'
            + `${e.message}\n\nBad Object:\n`, 'placementToolSavedData: ', placementToolSavedData, '\n'
            + 'troBenchmarkSavedData: ', troBenchmarkSavedData,
          );
        }
      }

      let { currentInterstitial } = state;
      if (!currentInterstitial) {
        currentInterstitial = INTERSTITIAL_IDS.INTRO;
      }

      return {
        ...state,
        didLoadSavedData: true,
        currentScreen: SCREENS.INTERSTITIAL,
        currentInterstitial,
        metrics: {
          ...state.metrics,
          localIPTEndReadingTime,
          localIPTEndLecturaTime,
        },
      };
    }

    case PERSIST_STATE_TYPES.POST_GAMESTATE: {
      // In case of post to tro_benchmark bucket
      if (stateData.benchmarkEOY || stateData?.gameDetails?.teacherResetInfo) {
        try {
          return mergeBenchmarkData(state, stateData);
        } catch (e) {
          console.error(
            'TRO Benchmark: There was a problem with the saved data sent to server.\n'
            + `${e.message}\n\nBad Object:\n`, stateData,
          );
        }
      }

      try {
        utility.validatePlacementToolData(stateData);
        return mergeData(state, stateData);
      } catch (e) {
        console.error(
          'Placement tool: There was a problem with the saved data sent to server.\n'
          + `${e.message}\n\nBad Object:\n`, stateData,
        );
      }

      return state;
    }

    case ACTIONS.BEGIN_NEXT_MEASURE: {
      if (state.measuresRemaining.length > 0) {
        const measuresRemaining = [...state.measuresRemaining];
        let { measureInProgress, metrics } = state;
        if (Object.keys(measureInProgress).length === 0) {
          measureInProgress = measuresRemaining.shift();
        }

        const currentMeasureKey = `${measureInProgress.id}_${measureInProgress.curriculum}`;
        const measureStartTimeKey = `${currentMeasureKey}_StartTime`;
        metrics = {
          ...metrics,
          [measureStartTimeKey]: getTimestamp(),
          currentMeasureKey,
        };
        return {
          ...state,
          measureInProgress,
          measuresRemaining,
          currentMeasure: measureInProgress,
          currentScreen: SCREENS.MEASURE,
          metrics,
        };
      }
      return {
        ...state,
        currentScreen: SCREENS.MEASURE,
      };
    }

    case ACTIONS.COMPLETE_CURRENT_MEASURE: {
      let { measureInProgress } = state;
      if (!Object.keys(measureInProgress).length) {
        return state;
      }
      const results = [...state.results];
      results.push({
        ...state.measureInProgress,
        score: payload.score,
        ...payload,
      });

      measureInProgress = {};
      const measuresCompleted = [...state.measuresCompleted];
      measuresCompleted.push(state.measureInProgress);

      // total measures this player will see in IPT
      const totalMeasures =
        measuresCompleted.length + state.measuresRemaining.length;
      // how many interstitials between these measures
      const totalMiddleInterstitials = totalMeasures - 1;
      // next to outro by default
      let currentInterstitial = INTERSTITIAL_IDS.OUTRO;
      // Don't update currentInterstitial if (edge case) there's already TRO data and only need RWR (last measure)
      // or if it's benchmark EOY (not for placement).
      // If we have measures remaining, find which middle interstitial to show
      if (state.measuresRemaining.length > 0 && !state.troData.is_complete && !state.benchmarkEOY) {
        // it's the total middle interstitials minus the count of remaining measures
        currentInterstitial = INTERSTITIAL_IDS.MIDDLE[totalMiddleInterstitials - state.measuresRemaining.length];
        if (totalMiddleInterstitials > 4) { // right now this is only for Lectura APT grade 1
          // TODO add product to the action payload for lectura specific logic here
          currentInterstitial = INTERSTITIAL_IDS.MIDDLE[1];
        }
      }
      if (state.benchmarkEOY === ASSESSMENT_PATHS.ARBA) {
        currentInterstitial = INTERSTITIAL_IDS.MIDDLE[0];
      }

      if (state.measureInProgress.id === measureIds.BLENDING) {
        currentInterstitial = INTERSTITIAL_IDS.BUMPER_GEAR;
      } else if (state.measureInProgress.id === measureIds.SEGMENTING) {
        currentInterstitial = INTERSTITIAL_IDS.BUMPER_PAINT;
      } else if (state.measureInProgress.id === measureIds.LETTER_SOUND_CORRESPONDENCES) {
        currentInterstitial = INTERSTITIAL_IDS.BUMPER_LIGHTS;
      } else if (state.measureInProgress.id === measureIds.SYLLABLE_DECODING) {
        currentInterstitial = INTERSTITIAL_IDS.MIDDLE[0];
      }

      return {
        ...state,
        currentMeasure: {},
        measureInProgress,
        measuresCompleted,
        currentScreen: SCREENS.INTERSTITIAL,
        currentInterstitial,
        hasTroError: payload.hasTroError,
        results,
      };
    }

    case ACTIONS.UPDATE_IPT_METRICS: {
      const metrics = getUpdatedMetricsData(state);
      return {
        ...state,
        metrics,
      };
    }

    case ACTIONS.UPDATE_COMPLETE_IPT_METRICS: {
      let { metrics: { localIPTEndReadingTime, localIPTEndLecturaTime } } = state;
      if (payload === PRODUCT_LECTURA) {
        localIPTEndLecturaTime = getTimestamp();
      } else {
        localIPTEndReadingTime = getTimestamp();
      }

      return {
        ...state,
        metrics: {
          ...state.metrics,
          localIPTEndReadingTime,
          localIPTEndLecturaTime,
        },
      };
    }

    case ACTIONS.SET_ROUND_SESSION_ID: {
      return {
        ...state,
        roundSessionIds: {
          ...state.roundSessionIds,
          ...payload,
        },
      };
    }

    case ACTIONS.SET_TRO_REQUEST: {
      const { benchmarkEOY, isBenchmarkModeOn, measuresCompleted } = state;
      let { currentInterstitial } = state;
      const { hasTroError, isTroRequestResolved, isTroRequestFailure, troData } = payload;

      if (measuresCompleted.length || isBenchmarkModeOn) {
        // hasTroError is only true for this action when in Benchmark Mode.
        // UX if isTroRequestFailure: assume TRO is complete and show OUTRO.
        //   If TRO was not complete: upon login with !isTroRequestFailure, show WELCOME_BACK and redirect to TRO.
        //   This scenario should be rare but if it becomes a common issue, create an API Error Interstitial.
        if (troData.is_complete || !!hasTroError || isTroRequestFailure) {
          currentInterstitial = INTERSTITIAL_IDS.OUTRO;
        } else if (!benchmarkEOY) {
          currentInterstitial = INTERSTITIAL_IDS.WELCOME_BACK;
        }
      }

      return {
        ...state,
        currentInterstitial,
        isTroRequestResolved,
        isTroRequestFailure,
        troData,
      };
    }

    case ACTIONS.RESET_BENCHMARK_EOY: {
      return {
        ...state,
        benchmarkEOY: null,
        hasStartedBenchmarkEOY: false,
        hasCompletedRWR: false,
      };
    }

    case ACTIONS.SET_RWR_COMPLETE: {
      let { measuresCompleted, measureInProgress, measuresRemaining } = state;

      if (payload) {
        measuresCompleted = [payload];
        measureInProgress = {};
        measuresRemaining = [];
      }

      return {
        ...state,
        hasCompletedRWR: true,
        measuresCompleted,
        measureInProgress,
        measuresRemaining,
      };
    }

    case ACTIONS.SET_IS_VALID_TRO_RESULT: {
      return {
        ...state,
        isValidTroResult: payload,
        currentInterstitial: INTERSTITIAL_IDS.INVALID_TRO_RESULT,
      };
    }

    case ACTIONS.SET_BENCHMARK_MODE_REDIRECT_FLAG: {
      return {
        ...state,
        isBenchmarkModeOn: payload,
      };
    }

    case ACTIONS.RESET_BENCHMARK_MODE_REDIRECT_FLAG: {
      const timeOfYear = payload;
      return {
        ...state,
        isBenchmarkModeOn: false,
        teacherResetInfo: {
          ...state.teacherResetInfo,
          [timeOfYear]: {
            ...state.teacherResetInfo[timeOfYear],
            teacherReset: false,
          },
        },
      };
    }

    case ACTIONS.SET_TRO_ERROR: {
      const timeOfYear = payload;
      return {
        ...state,
        teacherResetInfo: {
          ...state.teacherResetInfo,
          [timeOfYear]: {
            ...state.teacherResetInfo[timeOfYear],
            hasTroError: true,
          },
        },
      };
    }

    case ACTIONS.SET_SHOULD_DISPLAY_AMP_GLOBAL_NAV: {
      return {
        ...state,
        shouldDisplayAmpGlobalNav: payload,
      };
    }

    case PERSIST_STATE_TYPES.REQUESTERROR_GAMESTATE:
    default:
      return state;
  }
};

export const mergeData = (state, savedData = {}) => {
  const {
    version,
    results,
    currentMeasure,
    measureInProgress,
    measuresCompleted,
    measuresRemaining,
    placements,
    metrics,
    rwrId,
    mtgIdStory1,
    mtgIdStory2,
    benchmarkId,
  } = savedData;
  return {
    ...state,
    version: version || state.version,
    results: results || state.results,
    currentMeasure: currentMeasure || state.currentMeasure,
    placements: placements || state.placements,
    measureInProgress: measureInProgress || state.measureInProgress,
    measuresCompleted: measuresCompleted || state.measuresCompleted,
    measuresRemaining: measuresRemaining || state.measuresRemaining,
    metrics: metrics || state.metrics,
    roundSessionIds: {
      rwrId: rwrId || state.roundSessionIds.rwrId,
      mtgIdStory1: mtgIdStory1 || state.roundSessionIds.mtgIdStory1,
      mtgIdStory2: mtgIdStory2 || state.roundSessionIds.mtgIdStory2,
      benchmarkId: benchmarkId || state.roundSessionIds.benchmarkId,
    },
  };
};

export const mergeBenchmarkData = (state, savedData = {}) => {
  const {
    benchmarkEOY,
    hasStartedBenchmarkEOY,
    hasCompletedRWR,
    rwrId,
    benchmarkId,
  } = savedData;
  return {
    ...state,
    benchmarkEOY: benchmarkEOY || state.benchmarkEOY,
    hasStartedBenchmarkEOY: hasStartedBenchmarkEOY || state.hasStartedBenchmarkEOY,
    hasCompletedRWR: hasCompletedRWR || state.hasCompletedRWR,
    roundSessionIds: {
      ...state.roundSessionIds,
      rwrId: rwrId || state.roundSessionIds.rwrId,
      benchmarkId: benchmarkId || state.roundSessionIds.benchmarkId,
    },
    teacherResetInfo: mergeTeacherResetInfo(state.teacherResetInfo, savedData),
  };
};

const mergeTeacherResetInfo = (currResetState, resetInfo = {}) => {
  const savedData = resetInfo?.gameDetails?.teacherResetInfo;
  if (savedData) {
    return Object.assign(currResetState, savedData);
  }

  return currResetState;
};
